import { useEffect } from 'react';
import { QrCode, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { Channel } from '@conversia/shared-types';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useChannelsStore } from '@/store/channels.store';

/** Tras conectar, el modal se cierra solo pasado este lapso (ms). */
const AUTO_CLOSE_MS = 1_800;

/**
 * Muestra el QR de vinculación de WhatsApp. El QR y el estado en vivo llegan por
 * WebSocket (`channel.status` → store). Cuando el canal pasa a CONNECTED el modal
 * muestra el éxito y se cierra solo; siempre hay un botón "Cerrar" disponible.
 */
export function QrModal({
  channel,
  open,
  onClose,
}: {
  channel: Channel | null;
  open: boolean;
  onClose: () => void;
}) {
  const qr = useChannelsStore((s) => (channel ? s.qrByChannel[channel.id] : undefined));
  const error = useChannelsStore((s) => (channel ? s.errorByChannel[channel.id] : undefined));
  const status = useChannelsStore((s) => (channel ? s.statusByChannel[channel.id] : undefined));
  const isImage = qr?.startsWith('data:image');
  const isConnected = status === 'CONNECTED';
  const isSyncing = status === 'CONNECTING' || status === 'RECONNECTING';

  // Al conectar, cerrar el modal automáticamente tras un breve éxito visible.
  useEffect(() => {
    if (!open || !isConnected) return;
    const t = setTimeout(onClose, AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [open, isConnected, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Conectar ${channel?.name ?? 'canal'}`}
      description="Abre WhatsApp › Dispositivos vinculados › Vincular dispositivo y escanea el código."
      size="sm"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {isConnected ? 'Listo' : 'Cerrar'}
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-4 py-2">
        <div className="flex h-56 w-56 items-center justify-center rounded-lg border border-surface-border bg-white p-3 dark:border-slate-700">
          {error ? (
            <div className="flex flex-col items-center gap-3 px-2 text-center text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-16 w-16" strokeWidth={1} />
              <span className="text-xs">{error}</span>
            </div>
          ) : isConnected ? (
            <div className="flex flex-col items-center gap-3 text-center text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-16 w-16" strokeWidth={1.5} />
              <span className="text-sm font-medium">¡Conectado!</span>
            </div>
          ) : isImage ? (
            <img src={qr} alt="QR de conexión" className="h-full w-full object-contain" />
          ) : qr ? (
            <pre className="max-h-full overflow-auto whitespace-pre-wrap break-all text-[6px] leading-[7px] text-slate-700">
              {qr}
            </pre>
          ) : isSyncing ? (
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <Loader2 className="h-16 w-16 animate-spin" strokeWidth={1} />
              <span className="text-xs">Sincronizando WhatsApp…</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-slate-300">
              <QrCode className="h-20 w-20" strokeWidth={1} />
              <span className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="h-3 w-3 animate-spin" /> Esperando QR…
              </span>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-400">
          {isConnected
            ? 'El canal quedó vinculado. Esta ventana se cierra sola.'
            : 'El código se actualiza automáticamente cuando el servidor lo emite por WebSocket.'}
        </p>
      </div>
    </Modal>
  );
}
